import createFetchClient from "openapi-fetch";
import createClient from "openapi-react-query";
import type { paths } from "./api-types";
import { auth } from "./firebase";
import { useEffect, useState } from "react";
import { getApiBaseUrl } from "./config";

export const fetchRequestWithAuth = async (request: Request) => {
  const user = auth.currentUser;
  const headers = new Headers(request.headers);

  if (user) {
    const token = await user.getIdToken();
    headers.set("Authorization", `Bearer ${token}`);
  }

  return fetch(new Request(request, { headers }));
};

const fetchClient = createFetchClient<paths>({
  baseUrl: getApiBaseUrl(),
  fetch: async (input: Request, init?: RequestInit) => {
    const baseHeaders = input instanceof Request ? input.headers : undefined;
    const headers = new Headers(baseHeaders);

    if (init?.headers) {
      for (const [key, value] of new Headers(init.headers).entries()) {
        headers.set(key, value);
      }
    }

    return fetchRequestWithAuth(new Request(input, { ...init, headers }));
  },
});

// Export React Query client for hook-based usage
export const apiClient = createClient<paths>(fetchClient);

/**
 * Hook to fetch the current user's profile from the backend.
 * Only runs if Firebase has an authenticated user.
 */
export const useUser = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(!!auth.currentUser);
  const [isAuthChecking, setIsAuthChecking] = useState(true);

  // Sync state with Firebase's internal auth state
  useEffect(() => {
    return auth.onAuthStateChanged((user) => {
      setIsAuthenticated(!!user);
      setIsAuthChecking(false);
    });
  }, []);

  const query = apiClient.useQuery(
    "get",
    "/api/user/me",
    {},
    {
      enabled: !isAuthChecking && isAuthenticated,
      staleTime: 1000 * 60 * 5,
      retry: false,
    },
  );

  return {
    ...query,
    data: query.data && "user" in query.data ? query.data.user : query.data,
    isLoading: isAuthChecking || query.isLoading,
  };
};
